import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { LocalUpdateStatusDto, OrgOfflineAccessDto, TenantLicenceCardDto, UpdatesApplyBody } from '@his/shared';
import { get, post } from '@/lib/api';
import { visibleInterval } from '@/lib/polling';

export const UPDATE_STATUS_KEY = ['updates', 'status'] as const;
/** The banner re-asks the server this often; a check-in happens every 5 minutes, so nothing is missed for long. */
export const UPDATE_STATUS_POLL_MS = 10 * 60_000;
export const ABOUT_SETTINGS_PATH = '/admin/settings?tab=about';

/** `GET /updates/status` — public on the API, but sent through the staff client so the tenant header rides along. */
export function fetchUpdateStatus(): Promise<LocalUpdateStatusDto> {
  return get<LocalUpdateStatusDto>('/updates/status');
}

export function useUpdateStatusQuery(options: { enabled?: boolean; refetchInterval?: number | false | (() => number | false) } = {}): UseQueryResult<LocalUpdateStatusDto> {
  return useQuery({
    queryKey: UPDATE_STATUS_KEY,
    queryFn: fetchUpdateStatus,
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval ?? visibleInterval(UPDATE_STATUS_POLL_MS),
    staleTime: 60_000,
    retry: false,
  });
}

export function useCheckForUpdatesMutation(): UseMutationResult<LocalUpdateStatusDto, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => post<LocalUpdateStatusDto>('/updates/check-now'),
    onSuccess: (data) => queryClient.setQueryData(UPDATE_STATUS_KEY, data),
  });
}

export function useApplyUpdateMutation(): UseMutationResult<{ started: boolean }, unknown, UpdatesApplyBody> {
  return useMutation({
    mutationFn: (body: UpdatesApplyBody) => post<{ started: boolean }>('/updates/apply', body),
  });
}

export const OFFLINE_ACCESS_KEY = ['org', 'offline-access'] as const;

/** `GET /org/settings/offline-access` — whether the owner allows offline mode for this hospital (docs/OFFLINE-MODE.md step 0). */
export function useOfflineAccessQuery(): UseQueryResult<OrgOfflineAccessDto> {
  return useQuery({
    queryKey: OFFLINE_ACCESS_KEY,
    queryFn: () => get<OrgOfflineAccessDto>('/org/settings/offline-access'),
    staleTime: 60_000,
    retry: false,
  });
}

/** The hospital's own copy of the landing-page download PIN (docs/SYNC.md); shown once. */
export function useRegenerateDownloadPinMutation(): UseMutationResult<{ pin: string }, unknown, void> {
  return useMutation({
    mutationFn: () => post<{ pin: string }>('/org/settings/download-pin'),
  });
}

export const LICENCE_CARD_KEY = ['updates', 'licence'] as const;

export function useLicenceCardQuery(): UseQueryResult<TenantLicenceCardDto> {
  return useQuery({
    queryKey: LICENCE_CARD_KEY,
    queryFn: () => get<TenantLicenceCardDto>('/updates/licence'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useGraceCodeMutation(): UseMutationResult<{ until: string }, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => post<{ until: string }>('/updates/grace-code', { code }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LICENCE_CARD_KEY });
      void queryClient.invalidateQueries({ queryKey: UPDATE_STATUS_KEY });
    },
  });
}
